import { Effect, Option } from 'effect';

import { Cart } from '../../capability/cart/Cart';
import type {
  ApplyCartCoupon,
  RemoveCartItem,
  SetCartItemQuantity,
} from '../../capability/cart/Cart';
import { writeIntoCart } from '../../capability/cart/cartToken';
import { confirmCartWrite } from './confirmCartWrite';

/** Set an existing Cart line to an absolute quantity. */
export const setCartItemQuantity = (input: Omit<SetCartItemQuantity, 'token'>) =>
  writeIntoCart((token) =>
    confirmCartWrite(
      token,
      Cart.pipe(Effect.flatMap((cart) => cart.setItemQuantity({ ...input, token }))),
      (current) =>
        current.items.some((item) => item.id === input.itemId && item.quantity === input.quantity)
    )
  );

/** Remove an existing Cart line. */
export const removeCartItem = (input: Omit<RemoveCartItem, 'token'>) =>
  writeIntoCart((token) =>
    confirmCartWrite(
      token,
      Cart.pipe(Effect.flatMap((cart) => cart.removeItem({ ...input, token }))),
      (current) => current.items.every((item) => item.id !== input.itemId)
    )
  );

/** Apply a coupon and confirm its code on the Cart. */
export const applyCartCoupon = (input: Omit<ApplyCartCoupon, 'token'>) =>
  writeIntoCart((token) =>
    confirmCartWrite(
      token,
      Cart.pipe(Effect.flatMap((cart) => cart.applyCoupon({ ...input, token }))),
      (current) => Option.exists(current.coupon, (coupon) => coupon.code === input.code)
    )
  );

/** Remove the Cart coupon and confirm its absence. */
export const removeCartCoupon = writeIntoCart((token) =>
  confirmCartWrite(
    token,
    Cart.pipe(Effect.flatMap((cart) => cart.removeCoupon({ token }))),
    (current) => Option.isNone(current.coupon)
  )
);
